import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { AuditLogOptions, AuditLogSummary, AuditLogSummaryStore } from 'src/stores/audit-logs-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Card, Row, Tag } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';
import * as Moment from 'moment';
import ExportButton from 'src/common/components/export-button';
import { ENV } from 'src/';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { AppConfiguration } from 'src/core/services/configuration.service';
import i18next from 'i18next';

const AuditLogsListPage: FC = () => {
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'timestamp', direction: 'Descending', useProfile: false }],
    skip: 0,
    take: 10,
  } as Query);

  const logsStore = container.get(AuditLogSummaryStore);
  const logsState = logsStore.state;

  setHeader(t('AuditLogs'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'audit-logs', breadcrumbName: t('AuditLogs') },
  ]);

  useEffect(
    () => {
      logsStore.load(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        // searchable: false,
        filter: <DateRangeFilter />,
        fiterField: 'timestamp',
        field: 'timestamp',
        title: t('Timestamp'),
        renderer: (data) => <span>{Moment.utc(data.timestamp).local().locale(i18next.language).format(`${getDateFormat()}, hh:mm a`)}</span>,
      },
      {
        searchable: true,
        sortable: true,
        sortField: 'UserDisplayName',
        useProfile: true,
        field: 'UserDisplayName',
        title: t('User'),
        renderer: (data) => <span>{data.userDisplayName}</span>,
      },
      {
        sortable: true,
        filter: <OptionsFilter options={AuditLogOptions.map((v) => ({ label: t(v.label), value: v.value }))} />,
        fiterField: 'type',
        field: 'type',
        title: t('LogType'),
        renderer: (data: AuditLogSummary) => <Tag>{t(data.type)}</Tag>,
      },
      {
        searchable: true,
        field: 'description',
        title: t('Description'),
        renderer: (data) => <span>{data.description}</span>,
      },
      {
        sortable: true,
        searchable: true,
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={logsStore.baseUrl} fieldName={'relatedEntityTitle'} fieldValue={'relatedEntityTitle'} />,
        fiterField: 'relatedEntityTitle',
        field: 'relatedEntityTitle',
        title: t('RelatedEntity'),
        renderer: (data) => <span>{data.relatedEntityTitle}</span>,
      },
      {
        sortable: true,
        searchable: true,
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={logsStore.baseUrl} fieldName={'Organization/DisplayName'} fieldValue={'organizationDisplayName'} />,
        fiterField: 'Organization/DisplayName',
        field: 'Organization/DisplayName',
        title: t('Organization'),
        renderer: (data) => <span>{data.organizationDisplayName}</span>,
      },
    ],
    data: { ...logsState.value, isBusy: logsState.value.isBusy },
  } as TableModel<AuditLogSummary>;

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table" style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew={false}
          canDelete={false}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(query);
            logsStore.load(query);
          }}
          onRefresh={() => logsStore.load(query)}
          model={logsState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            logsState.errorMessage.value && (config.environment === ENV.DEV ? formatMessage(logsState.errorMessage.value) : t('AuditLogsTableError'))
          }
          rightToolbar={
            <li>
              <ExportButton
                itemsCount={logsState.count.get()}
                maxItemsCount={500}
                onExport={() => logsStore.exportCsv(query)}
                tooltip={t('ExportCSV')}
              />
            </li>
          }
        />
      </Card>
    </Row>
  );
};

export default AuditLogsListPage;
